import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { invoiceApi, reservationApi } from '../../services/api';
import { EmailLog } from '../../types/emailLog';
import { formatDateTime } from '../../utils/formatters';

interface EmailLogPanelProps {
  documentType: 'reservation' | 'invoice';
  documentId: string;
}

export const EmailLogPanel: React.FC<EmailLogPanelProps> = ({ documentType, documentId }) => {
  const { t } = useTranslation();
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

  const logsQuery = useQuery<EmailLog[]>(
    ['emailLogs', documentType, documentId],
    () => (documentType === 'reservation' ? reservationApi.getEmailLogs(documentId) : invoiceApi.getEmailLogs(documentId)),
    { enabled: !!documentId }
  );

  if (logsQuery.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const logs = logsQuery.data ?? [];

  if (logs.length === 0) {
    return <Alert severity="info">{t('emailLog.empty', 'No emails have been sent yet.')}</Alert>;
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('emailLog.columns.sentOn', 'Sent On')}</TableCell>
              <TableCell>{t('emailLog.columns.sentBy', 'Sent By')}</TableCell>
              <TableCell>{t('emailLog.columns.to', 'To')}</TableCell>
              <TableCell>{t('emailLog.columns.subject', 'Subject')}</TableCell>
              <TableCell align="center">{t('emailLog.columns.status', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id} hover onClick={() => setSelectedLog(log)} sx={{ cursor: 'pointer' }}>
                <TableCell>{formatDateTime(log.sentOn)}</TableCell>
                <TableCell>{log.sentByDisplayName}</TableCell>
                <TableCell>{log.recipientEmail}</TableCell>
                <TableCell>{log.subject}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    color={log.success ? 'success' : 'error'}
                    label={log.success ? t('emailLog.sent', 'Sent') : t('emailLog.failed', 'Failed')}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selectedLog)} onClose={() => setSelectedLog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('emailLog.detailsTitle', 'Sent Email')}</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Chip
                size="small"
                color={selectedLog.success ? 'success' : 'error'}
                label={selectedLog.success ? t('emailLog.sent', 'Sent') : t('emailLog.failed', 'Failed')}
                sx={{ alignSelf: 'flex-start' }}
              />
              <Typography variant="body2" color="text.secondary">
                {t('emailLog.columns.sentOn', 'Sent On')}: {formatDateTime(selectedLog.sentOn)} | {selectedLog.sentByDisplayName}
              </Typography>
              {selectedLog.templateName && (
                <Typography variant="body2" color="text.secondary">
                  {t('emailLog.template', 'Template')}: {selectedLog.templateName}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>{t('emailLog.columns.to', 'To')}:</strong> {selectedLog.recipientEmail}
              </Typography>
              <Typography variant="body2">
                <strong>{t('emailLog.columns.subject', 'Subject')}:</strong> {selectedLog.subject}
              </Typography>
              {selectedLog.attachmentFileName && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachFile fontSize="small" /> {selectedLog.attachmentFileName}
                </Typography>
              )}
              {!selectedLog.success && selectedLog.errorMessage && (
                <Alert severity="error">{selectedLog.errorMessage}</Alert>
              )}
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 320,
                  overflowY: 'auto',
                }}
                dangerouslySetInnerHTML={{ __html: selectedLog.body }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>{t('common.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
